import React, { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import { object, number, bool } from "prop-types";
import {
  format,
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
  startOfWeek,
  endOfWeek,
  isSameISOWeek,
  startOfMonth,
  endOfMonth
} from "date-fns";
import DatePicker from "react-datepicker";

import StorySideBar from "../story-sidebar";
import AuthorDefaultLogo from "../../atoms/author-default-img";
import StoryDate from "../../atoms/story-date";
import Separator from "../../atoms/separator";
import { AdContainer } from "../../ads";

import SocialMediaShare from "../../molecules/social-media-share";
import { getAdvancedSearchStories } from "../../helper/api";

import "./horoscope.m.css";
import useAdType from "../../helper/useAdType";
import { ResponsiveImage } from "@quintype/components";
import TaboolaAd from "../../ads/taboola-ad";
import { throttle } from "lodash";
import Share from "../../atoms/other-icons/Share";
import HoroscopeMobileCard from "../../atoms/horoscope-mobile-card";
import HoroscopePopup from "../../atoms/horoscope-popup";

const ZodiacIcon = ({ slug, metadata }) => (
  <div styleName="zodiac-icon-wrapper" className="zodiac-icon">
    <ResponsiveImage slug={slug} metadata={metadata} widths={[100]} />
  </div>
);

const filterOptions = [
  {
    name: "Daily",
    value: "horoscope-daily"
  },
  {
    name: "Weekly",
    value: "horoscope-weekly"
  },
  // {
  //   name: "Monthly",
  //   value: "horoscope-monthly"
  // },
  {
    name: "Yearly",
    value: "horoscope-yearly"
  }
];
const getHoroscopeFilterTag = (tags) => {
  return tags.find(
    (tag) =>
      tag?.slug === "horoscope-daily" ||
      tag?.slug === "horoscope-weekly" ||
      tag?.slug === "horoscope-monthly" ||
      tag?.slug === "horoscope-yearly"
  );
};

const HoroscopeStoryTemplate = ({ story, index, isWebView = false }) => {
  const dispatch = useDispatch();
  const horoscope_icons = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "horoscope_icons"])
  );

  const storyFilter = getHoroscopeFilterTag(story.tags);

  const publishedDate = get(story, ["published-at"]);

  const storyPublishedDate = format(new Date(publishedDate), "dd MMMM yyyy, HH:mm");
  const defaultStoryDate = publishedDate ? new Date(publishedDate) : new Date();

  const [filterType, setFilterType] = useState(storyFilter?.slug);
  const [filterDate, setFilterDate] = useState(defaultStoryDate);

  const [filterOption, setFilterOption] = useState(storyFilter?.slug);
  const [toggleSharing, setToggleSharing] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [updateStory, setUpdateStory] = useState(story);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        toggleSharing !== false && toggleSharing !== undefined &&
        !event.target.closest('[class*="share-wrapper"]') &&
        !event.target.closest('[class*="social-share-wrapper"]') &&
        !event.target.closest('[class*="share-options"]') &&
        !event.target.closest('button[class*="share"]') &&
        !event.target.closest('svg')
      ) {
        setToggleSharing(undefined);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [toggleSharing]);
  const [openPopup, setOpenPopup] = useState(false);

  // ref to store scroll position when popup opens so we can restore it
  const scrollYRef = useRef(0);

  const preventTouchMove = useCallback((e) => {
    // prevent background scrolling on touch devices (older iOS)
    e.preventDefault();
  }, []);

  const lockBody = useCallback(() => {
    if (typeof window === "undefined") return;
    scrollYRef.current = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    // don't change documentElement (html) overflow — on some browsers (older iOS)
    // this can hide the whole page. Restrict overflow change to body only.
    document.body.style.overflow = "hidden";
    document.addEventListener("touchmove", preventTouchMove, { passive: false });
  }, [preventTouchMove]);

  const unlockBody = useCallback(() => {
    if (typeof window === "undefined") return;
    document.removeEventListener("touchmove", preventTouchMove);
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    // restore body overflow only (avoid touching html element)
    document.body.style.overflow = "";
    const y = scrollYRef.current || 0;
    window.scrollTo(0, y);
  }, [preventTouchMove]);

  const author = get(story, ["authors", "0"], {});
  const authorImg = author["avatar-url"] || author["avatar-s3-key"];

  const preventKeyboardHandler = (e) => (e.target.readOnly = true);

  const [isMobile, setMobile] = useState(false);
  const adType = useAdType();
  const [renderTaboolaAd, setRenderTaboolaAd] = useState(false);

  useEffect(() => {
    const getScroll = () => {
      if (typeof window !== "undefined") {
        if (window.scrollY > 500) setRenderTaboolaAd(true);
      }
    };
    window.addEventListener("scroll", throttle(getScroll, 500));
    return () => window.removeEventListener("scroll", getScroll);
  }, []);

  const Horoscopes = [
    {
      icon: <ZodiacIcon slug={horoscope_icons.aries?.["hero-image-s3-key"] || horoscope_icons.aries} metadata={horoscope_icons.aries?.["hero-image-metadata"]} />,
      dateRange: "March 21 - April 20",
      name: "Aries",
      key: "aries"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.taurus?.["hero-image-s3-key"] || horoscope_icons.taurus} metadata={horoscope_icons.taurus?.["hero-image-metadata"]} />,
      dateRange: "April 21 - May 21",
      name: "Taurus",
      key: "taurus"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.gemini?.["hero-image-s3-key"] || horoscope_icons.gemini} metadata={horoscope_icons.gemini?.["hero-image-metadata"]} />,
      dateRange: "May 22 - June 21",
      name: "Gemini",
      key: "gemini"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.cancer?.["hero-image-s3-key"] || horoscope_icons.cancer} metadata={horoscope_icons.cancer?.["hero-image-metadata"]} />,
      dateRange: "June 22 - July 22",
      name: "Cancer",
      key: "cancer"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.leo?.["hero-image-s3-key"] || horoscope_icons.leo} metadata={horoscope_icons.leo?.["hero-image-metadata"]} />,
      dateRange: "July 23 - August 21",
      name: "Leo",
      key: "leo"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.virgo?.["hero-image-s3-key"] || horoscope_icons.virgo} metadata={horoscope_icons.virgo?.["hero-image-metadata"]} />,
      dateRange: "August 22 - September 23",
      name: "Virgo",
      key: "virgo"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.libra?.["hero-image-s3-key"] || horoscope_icons.libra} metadata={horoscope_icons.libra?.["hero-image-metadata"]} />,
      dateRange: "September 23 - October 22",
      name: "Libra",
      key: "libra"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.scorpio?.["hero-image-s3-key"] || horoscope_icons.scorpio} metadata={horoscope_icons.scorpio?.["hero-image-metadata"]} />,
      dateRange: "October 23 - November 21",
      name: "Scorpio",
      key: "scorpio"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.sagittarius?.["hero-image-s3-key"] || horoscope_icons.sagittarius} metadata={horoscope_icons.sagittarius?.["hero-image-metadata"]} />,
      dateRange: "November 22 - December 21",
      name: "Sagittarius",
      key: "sagittarius"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.capricon?.["hero-image-s3-key"] || horoscope_icons.capricon} metadata={horoscope_icons.capricon?.["hero-image-metadata"]} />,
      dateRange: "December 23 - January 20",
      name: "Capricorn",
      key: "capricorn"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.aquarius?.["hero-image-s3-key"] || horoscope_icons.aquarius} metadata={horoscope_icons.aquarius?.["hero-image-metadata"]} />,
      dateRange: "January 21 - February 19",
      name: "Aquarius",
      key: "aquarius"
    },
    {
      icon: <ZodiacIcon slug={horoscope_icons.pisces?.["hero-image-s3-key"] || horoscope_icons.pisces} metadata={horoscope_icons.pisces?.["hero-image-metadata"]} />,
      dateRange: "February 20 - March 20",
      name: "Pisces",
      key: "pisces"
    }
  ];
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setOpenPopup(false);
        unlockBody();
      }
    };
    if (openPopup) {
      document.addEventListener("keydown", handleEsc);
    } else {
      document.removeEventListener("keydown", handleEsc);
    }
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (e) => setMobile(e.matches);
    setMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [openPopup]);
  useEffect(() => {
    if (window && window.innerWidth < 767) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  }, []);
  useEffect(() => {
    if (!isWebView) {
      const zodiacIcons = Array.from(document.querySelectorAll(".zodiac-icon")).map((card, index) => {
        card.setAttribute("id", index + 1);
        return card;
      });
      const seenElements = new Set();

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((card) => {
          const id = card.target.id;
          if (card.isIntersecting && !seenElements.has(id)) {
            seenElements.add(id);

            window.history.replaceState(null, null, `#${card.target.id}`);
            window.dataLayer.push({
              event: "horoscope_pageview",
              page: {
                url: window.location.href,
                title: document?.title
              }
            });
          }
        });
      });

      zodiacIcons.forEach((card) => {
        observer.observe(card);
      });

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (filterType === "horoscope-daily") {
      setUpdateStory((prevState) => ({
        ...prevState,
        "hero-image-s3-key":
          "/deccanherald-training/2023-07/ac09217c-bfbe-430c-ab69-54634324ffdb/horoscope_icon__1_.png"
      }));
    } else if (filterType === "horoscope-weekly") {
      setUpdateStory((prevState) => ({
        ...prevState,
        "hero-image-s3-key":
          "/deccanherald-training/2023-07/ac09217c-bfbe-430c-ab69-54634324ffdb/horoscope_icon__1_.png"
      }));
    } else if (filterType === "horoscope-monthly") {
      setUpdateStory((prevState) => ({
        ...prevState,
        "hero-image-s3-key":
          "/deccanherald-training/2023-07/ac09217c-bfbe-430c-ab69-54634324ffdb/horoscope_icon__1_.png"
      }));
    } else if (filterType === "horoscope-yearly") {
      setUpdateStory((prevState) => ({
        ...prevState,
        "hero-image-s3-key":
          "/deccanherald-training/2023-07/ac09217c-bfbe-430c-ab69-54634324ffdb/horoscope_icon__1_.png"
      }));
    }
  }, [filterType]);

  const getHoroscopeCard = (item, index) => {
    const text = story?.metadata?.[item.key] || "";
    const shareBtnHandler = async () => {
      const shareUrl = `${story.url}#${index + 1}`;
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: story?.headline,
            text: story?.headline,
            url: shareUrl
          });
        } catch (err) { }
        return;
      }
      setToggleSharing(toggleSharing === index ? undefined : index);
      setUpdateStory((prevState) => ({
        ...prevState,
        url: shareUrl
      }));
    };
    if (isMobile) {
      return (
        <HoroscopeMobileCard
          toggleSharing={toggleSharing}
          item={item}
          text={text}
          updateStory={updateStory}
          shareClickHandler={shareBtnHandler}
          index={index}
        />
      );
    }
    return (
      <div styleName="horoscope-item">
        <div styleName="icon-wrapper">
          <div styleName="horoscope-icon">{item.icon}</div>
        </div>

        <div styleName="horoscope-text">
          <div styleName="horoscope-name-wrapper">
            <div styleName="horoscope-name">{item.name}</div>

            <div styleName="share-wrapper">
              <div onClick={shareBtnHandler}>
                <Share color="black" height="24px" width="24px" />
              </div>
              <div styleName="share-options">{toggleSharing === index && <SocialMediaShare story={updateStory} />}</div>
            </div>
          </div>
          <div styleName="date-range">{item.dateRange}</div>
          <div styleName="horoscope-description">{text}</div>
        </div>
      </div>
    );
  };

  const navigateToPage = (url) => global.app.navigateToPage(dispatch, url);

  const getBeforeAndAfterDates = (filterType, dateValue) => {
    switch (filterType) {
      case "horoscope-daily": {
        const startDate = startOfDay(dateValue);
        const endDate = endOfDay(dateValue);
        return [Number(startDate), Number(endDate)];
      }
      case "horoscope-weekly": {
        const startDate = startOfWeek(dateValue);
        const endDate = endOfWeek(dateValue);
        return [Number(startDate), Number(endDate)];
      }
      case "horoscope-monthly": {
        const startDate = startOfMonth(dateValue);
        const endDate = endOfMonth(dateValue);
        return [Number(startDate), Number(endDate)];
      }
      case "horoscope-yearly": {
        const startDate = startOfYear(dateValue);
        const endDate = endOfYear(dateValue);
        return [Number(startDate), Number(endDate)];
      }
    }
  };

  const getFilterStories = (filterType, publishedAfter, publishedBefore) => {
    const params = {
      "tag-name": filterType,
      "published-after": publishedAfter,
      "published-before": publishedBefore
    };

    getAdvancedSearchStories(0, 1, params).then((items) => {
      if (items.length) {
        navigateToPage(`/${items[0]?.slug}`);
      } else {
        setShowMessage(true);
      }
    });
  };

  const handleDateFilter = (filterType) => (date) => {
    setFilterDate(date);
    const values = getBeforeAndAfterDates(filterType, date);
    getFilterStories(filterType, values[0], values[1]);
  };

  const handleTabClick = (type) => {
    setFilterType(type);
    handleDateFilter(type)(new Date());
  };

  const setFilterDropDown = (e) => {
    setFilterOption(e.target.value);
    handleTabClick(e.target.value);
  };

  const DateInput = forwardRef(({ onClick, startDate, endDate }, ref) => {
    return (
      <div styleName="datepicker-weekly" onClick={onClick} ref={ref}>{`${format(startDate, "dd/MM/yyyy")} - ${format(
        endDate,
        "dd/MM/yyyy"
      )}`}</div>
    );
  });

  const renderDateFilter = (filterType) => {
    switch (filterType) {
      case "horoscope-daily":
        return (
          <DatePicker
            selected={filterDate}
            onChange={handleDateFilter("horoscope-daily")}
            className="date-picker"
            dateFormat="dd MMM yyyy"
            maxDate={new Date()}
            showIcon
            onFocus={preventKeyboardHandler}
          />
        );
      case "horoscope-weekly":
        return (
          <div styleName="weekly-dropdown">
            <DatePicker
              selected={filterDate}
              startDate={startOfWeek(filterDate)}
              maxDate={endOfWeek(new Date())}
              endDate={endOfWeek(filterDate)}
              onChange={handleDateFilter("horoscope-weekly")}
              dayClassName={(date) => {
                isSameISOWeek(date, filterDate) ? "react-datepicker__day--selected" : "";
              }}
              showIcon
              onFocus={preventKeyboardHandler}
              customInput={<DateInput startDate={startOfWeek(filterDate)} endDate={endOfWeek(filterDate)} />}
            />
          </div>
        );
      case "horoscope-monthly":
        return (
          <DatePicker
            selected={filterDate}
            maxDate={endOfMonth(new Date())}
            onChange={handleDateFilter("horoscope-monthly")}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showIcon
            onFocus={preventKeyboardHandler}
          />
        );
      case "horoscope-yearly":
        return (
          <DatePicker
            selected={filterDate}
            maxDate={endOfYear(new Date())}
            onChange={handleDateFilter("horoscope-yearly")}
            showYearPicker
            dateFormat="yyyy"
            showIcon
            onFocus={preventKeyboardHandler}
          />
        );
    }
  };
  const onPopupClickHandler = () => {
    setOpenPopup(true);
    lockBody();
  };
  const onPopupCloseHandler = () => {
    setOpenPopup(false);
    unlockBody();
  };

  const scrollToZodiac = (id, offset) => {
    const element = document.getElementById(id);
    if (element) {
      // close popup and restore scroll
      setOpenPopup(false);
      unlockBody();
      const y = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
      window.history.replaceState(null, null, `#${id}`);
    }
  };
  const midAdsConfig = {
    3: {
      mobile: { id: "div-gpt-ad-1680157855094-0", slot: "DH_MWeb_AT_Mid", storyPageAds: true },
      desktop: { id: "div-gpt-ad-1680156704803-0", slot: "DH_Desktop_AT_Mid" }
    },
    6: {
      mobile: { id: "div-gpt-ad-1726651118047-0", slot: "DH_MWeb_AT_Mid02", storyPageAds: true },
      desktop: { id: "div-gpt-ad-1754393834239-0", slot: "DH_Desktop_AT_Mid02" }
    },
    9: {
      mobile: { id: "div-gpt-ad-1754032830774-0", slot: "DH_MWeb_AT_Mid03", storyPageAds: true },
      desktop: { id: "div-gpt-ad-1754393928790-0", slot: "DH_Desktop_AT_Mid03" }
    },
    12: {
      mobile: { id: "div-gpt-ad-1754033273317-0", slot: "DH_MWeb_AT_Mid04", storyPageAds: true },
      desktop: { id: "div-gpt-ad-1754393963082-0", slot: "DH_Desktop_AT_Mid04" }
    }
  };
  return (
    <div styleName="story-wrapper">
      <div styleName="story-content" className="story-section">
        <div styleName="top-filter-section">
          <div styleName="filters">
            <div
              onClick={() => handleTabClick("horoscope-daily")}
              styleName={`custom-button ${filterType === "horoscope-daily" ? "active" : ""}`}
            >
              Daily
            </div>
            <div
              onClick={() => handleTabClick("horoscope-weekly")}
              styleName={`custom-button ${filterType === "horoscope-weekly" ? "active" : ""}`}
            >
              Weekly
            </div>
            {/* <div
              onClick={() => handleTabClick("horoscope-monthly")}
              styleName={`custom-button ${filterType === "horoscope-monthly" ? "active" : ""}`}
            >
              Monthly
            </div> */}
            <div
              onClick={() => handleTabClick("horoscope-yearly")}
              styleName={`custom-button ${filterType === "horoscope-yearly" ? "active" : ""}`}
            >
              Yearly
            </div>
          </div>
          <select value={filterOption} onChange={setFilterDropDown} styleName="filter-dropdown">
            {filterOptions.map((item, index) => (
              <option key={index} value={item.value}>
                {item.name}
              </option>
            ))}
          </select>
          <div styleName="date-wrapper">{renderDateFilter(filterType)}</div>
        </div>

        {showMessage ? (
          <div styleName="no-stories">No results available</div>
        ) : (
          <>
            <div styleName="title">{story.headline}</div>

            <div styleName="published-date">Published {storyPublishedDate} IST</div>

            <div styleName="horoscope-wrapper">
              {Horoscopes.map((item, index) => {
                const horoscopeNumber = index + 1;
                const adConfig = midAdsConfig[horoscopeNumber];
                return (
                  <>
                    {getHoroscopeCard(item, index)}
                    {adConfig && (
                      <React.Fragment>
                        {isMobile === null ? null : isMobile === true ? (
                          <AdContainer
                            AdClassName="standard-336x280"
                            id={adType === "izooto" ? "div-gpt-ad-1711972030627-0" : adConfig.mobile.id}
                            mobile={adType === "izooto" ? "DH_MWeb_AT_Mid_ym" : adConfig.mobile.slot}
                            isExternalAdPath={adType === "izooto"}
                          />
                        ) : (
                          <AdContainer
                            AdClassName="standard-336x280"
                            id={adType === "izooto" ? "div-gpt-ad-1711971649563-0" : adConfig.desktop.id}
                            desktop={adType === "izooto" ? "DH_Desktop_AT_Mid_ym" : adConfig.desktop.slot}
                            isExternalAdPath={adType === "izooto"}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </>
                );
              })}
            </div>

            <Separator />

            <div styleName="author-name">{author.name}</div>

            <div styleName="author-block">
              <div styleName="author-image">
                {authorImg ? <img src={authorImg} alt="author" /> : <AuthorDefaultLogo width={140} height={140} />}
              </div>
              <div styleName="author-text">
                <span>{author.bio}</span>
              </div>
            </div>
            {!isWebView && isMobile && (
              <div styleName="ad-wrapper">
                <AdContainer
                  AdClassName="standard-336x280"
                  id="div-gpt-ad-1680157888251-0"
                  mobile="DH_MWeb_AT_Bottom"
                />
              </div>
            )}
          </>
        )}
        {renderTaboolaAd ? (
          <TaboolaAd
            container={`taboola-below-article-${story.id}`}
            placement="Below Article Thumbnails"
            mode="thumbnails-b"
          />
        ) : null}
      </div>

      {!isMobile && !isWebView ? (
        <div className="side-story-section hide-mobile">
          <StorySideBar />
        </div>
      ) : null}
      <div styleName="horoscope-yellow-widget" onClick={onPopupClickHandler}>
        <ResponsiveImage slug="dh-sandbox/2025-09-22/jb95u9ar/flotingbtn" metadata={{}} widths={[100]} />
      </div>
      <div styleName={`overlay ${openPopup ? "active" : ""}`} onClick={onPopupCloseHandler}>
        {openPopup && <HoroscopePopup scrollToZodiac={scrollToZodiac} />}
      </div>
    </div>
  );
};

HoroscopeStoryTemplate.propTypes = {
  story: object,
  index: number,
  isWebView: bool
};

const HoroscopeStory = (props) => {
  return <HoroscopeStoryTemplate story={props.story} index={props.index} isWebView={props.isWebView} />;
};

HoroscopeStory.propTypes = {
  story: object,
  index: number
};

export default HoroscopeStory;
